/**
 * Admin Shipments — Shiprocket configuration status + per-order shipment management.
 * Lists all orders and lets admin: create shipment, refresh tracking, cancel.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Truck, RefreshCw, Plus, X, Loader2, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";
import { ExportButtons } from "@/lib/exportData";
import Pagination, { usePagination } from "@/components/admin/Pagination";
import { toast } from "sonner";

export default function AdminShipments() {
    const [config, setConfig] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState({});
    const [filter, setFilter] = useState("all"); // all, paid, shipped, pending

    const loadConfig = async () => {
        try {
            const { data } = await api.get("/admin/shipments/config");
            setConfig(data);
        } catch {
            setConfig({ configured: false, auth_ok: false });
        }
    };

    const loadOrders = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/orders?limit=500");
            setOrders(Array.isArray(data) ? data : data.items || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadConfig();
        loadOrders();
    }, []);

    const filtered = orders.filter((o) => {
        if (filter === "all") return true;
        if (filter === "paid") return o.payment_status === "paid";
        if (filter === "shipped") return !!o.shiprocket?.awb_code;
        if (filter === "pending") return o.payment_status === "paid" && !o.shiprocket?.shipment_id;
        return true;
    });

    const { page, pageSize, totalPages, pageItems, setPage, setPageSize } = usePagination(filtered, 20);

    const createShipment = async (order_id) => {
        setBusy((b) => ({ ...b, [order_id]: "create" }));
        try {
            const { data } = await api.post(`/admin/shipments/create/${order_id}`);
            if (data.ok) {
                toast.success(`Shipment ${data.shiprocket?.shipment_id || ""} created`);
                loadOrders();
            } else {
                toast.error(`Failed: ${JSON.stringify(data.error).slice(0, 120)}`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy((b) => ({ ...b, [order_id]: null }));
        }
    };

    const refreshStatus = async (order_id) => {
        setBusy((b) => ({ ...b, [order_id]: "refresh" }));
        try {
            const { data } = await api.get(`/admin/shipments/track/${order_id}`);
            if (data.ok) {
                toast.success(`Status: ${data.status}`);
                loadOrders();
            } else {
                toast.error(`Track failed: ${data.error}`);
            }
        } finally {
            setBusy((b) => ({ ...b, [order_id]: null }));
        }
    };

    const cancelShipment = async (order_id) => {
        if (!window.confirm("Cancel this shipment in Shiprocket?")) return;
        setBusy((b) => ({ ...b, [order_id]: "cancel" }));
        try {
            const { data } = await api.post(`/admin/shipments/cancel/${order_id}`);
            if (data.ok) {
                toast.success("Shipment cancelled");
                loadOrders();
            } else {
                toast.error("Cancel failed");
            }
        } finally {
            setBusy((b) => ({ ...b, [order_id]: null }));
        }
    };

    const syncAll = async () => {
        const { data } = await api.post("/admin/shipments/sync-all");
        toast.success(`Synced ${data.synced}, failed ${data.failed}`);
        loadOrders();
    };

    const rows = filtered.map((o) => ({
        order_id: o.id,
        email: o.email,
        total: o.total,
        payment_status: o.payment_status,
        order_status: o.status,
        shiprocket_id: o.shiprocket?.shiprocket_order_id,
        awb: o.shiprocket?.awb_code,
        courier: o.shiprocket?.courier_name,
        shipment_status: o.shiprocket?.status,
        created_at: o.created_at,
    }));

    return (
        <div className="space-y-6" data-testid="admin-shipments">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#857C72]">
                        Shipping
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Shipments &amp; Shiprocket</h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={syncAll}
                        className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-xs font-mono-ui"
                        data-testid="shipments-sync-all"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Sync all statuses
                    </button>
                </div>
            </header>

            {/* Shiprocket config status */}
            <div className="rounded-2xl border border-[#E7DED2] bg-white p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-3">
                    <Truck className="h-5 w-5 text-[#1B1B1F]" />
                    <span className="font-mono-ui text-xs tracking-wide uppercase text-[#6B645C]">
                        Shiprocket
                    </span>
                    {config?.configured ? (
                        config.auth_ok ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-mono-ui border border-emerald-200">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Connected
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-mono-ui border border-amber-200">
                                <AlertTriangle className="h-3.5 w-3.5" /> Configured, but auth failed — check email/password in backend .env
                            </span>
                        )
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs font-mono-ui border border-red-200">
                            Not configured
                        </span>
                    )}
                    {config?.configured && (
                        <span className="text-xs text-[#6B645C]">
                            Pickup: <strong>{config.pickup_location}</strong> · Auto-create on paid: <strong>{config.auto_create_on_paid ? "on" : "off"}</strong>
                        </span>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <nav className="inline-flex p-1 rounded-full bg-[#FAF7F1] border border-[#E7DED2]">
                    {[
                        { k: "all", l: "All" },
                        { k: "paid", l: "Paid" },
                        { k: "pending", l: "Pending shipment" },
                        { k: "shipped", l: "With AWB" },
                    ].map((t) => (
                        <button
                            key={t.k}
                            onClick={() => setFilter(t.k)}
                            data-testid={`shipments-filter-${t.k}`}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-mono-ui transition-colors ${
                                filter === t.k ? "bg-[#1B1B1F] text-white" : "text-[#6B645C] hover:bg-white"
                            }`}
                        >
                            {t.l}
                        </button>
                    ))}
                </nav>
                <ExportButtons
                    rows={rows}
                    filename={`xplosiv_event_logistics_${filter}`}
                    columns={["order_id", "email", "total", "payment_status", "shiprocket_id", "awb", "courier", "shipment_status", "created_at"]}
                    className="ml-auto"
                />
            </div>

            {/* Table */}
            <div className="rounded-2xl border border-[#E7DED2] bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm" data-testid="shipments-table">
                        <thead className="bg-[#FAF7F1]">
                            <tr className="text-left">
                                {["Order", "Customer", "Total", "Payment", "AWB / Courier", "Status", "Actions"].map((h) => (
                                    <th key={h} className="px-4 py-3 font-mono-ui text-[10px] tracking-[0.16em] uppercase text-[#6B645C]">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E7DED2]">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-10 text-center text-[#857C72]">
                                        <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading…
                                    </td>
                                </tr>
                            ) : pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-10 text-center text-[#857C72]">
                                        No orders match.
                                    </td>
                                </tr>
                            ) : (
                                pageItems.map((o) => {
                                    const sr = o.shiprocket || {};
                                    const b = busy[o.id];
                                    return (
                                        <tr key={o.id} data-testid={`shipment-row-${o.id}`}>
                                            <td className="px-4 py-3 align-top">
                                                <div className="font-mono-ui text-xs">{o.id.slice(0, 8)}…</div>
                                                <div className="text-[11px] text-[#857C72]">{(o.created_at || "").slice(0, 10)}</div>
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                <div className="text-xs">{o.email || "—"}</div>
                                                <div className="text-[11px] text-[#857C72]">{(o.items || []).length} item(s)</div>
                                            </td>
                                            <td className="px-4 py-3 align-top font-mono-ui text-xs">₹{((o.total || 0) / 100).toFixed(0)}</td>
                                            <td className="px-4 py-3 align-top">
                                                <span className={`text-[11px] px-2 py-1 rounded-full font-mono-ui ${
                                                    o.payment_status === "paid"
                                                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                        : "bg-amber-50 text-amber-700 border border-amber-200"
                                                }`}>
                                                    {o.payment_status || "pending"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                {sr.awb_code ? (
                                                    <div>
                                                        <div className="font-mono-ui text-xs">{sr.awb_code}</div>
                                                        <div className="text-[11px] text-[#857C72]">{sr.courier_name || "—"}</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-[11px] text-[#857C72]">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                <span className="text-[11px] px-2 py-1 rounded-full bg-[#FAF7F1] border border-[#E7DED2] font-mono-ui">
                                                    {sr.status || "—"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                <div className="flex flex-wrap gap-1.5">
                                                    {!sr.shipment_id && o.payment_status === "paid" && (
                                                        <button
                                                            onClick={() => createShipment(o.id)}
                                                            disabled={b === "create"}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1B1B1F] text-white text-[11px] font-mono-ui hover:bg-black disabled:opacity-60"
                                                            data-testid={`create-shipment-${o.id}`}
                                                        >
                                                            {b === "create" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Create
                                                        </button>
                                                    )}
                                                    {sr.awb_code && (
                                                        <button
                                                            onClick={() => refreshStatus(o.id)}
                                                            disabled={b === "refresh"}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-[11px] font-mono-ui"
                                                            data-testid={`refresh-shipment-${o.id}`}
                                                        >
                                                            {b === "refresh" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Track
                                                        </button>
                                                    )}
                                                    {sr.track_url && (
                                                        <a
                                                            href={sr.track_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-[11px] font-mono-ui"
                                                        >
                                                            <ExternalLink className="h-3 w-3" />
                                                        </a>
                                                    )}
                                                    {sr.shipment_id && (
                                                        <button
                                                            onClick={() => cancelShipment(o.id)}
                                                            disabled={b === "cancel"}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600 text-[11px] font-mono-ui"
                                                            data-testid={`cancel-shipment-${o.id}`}
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Pagination page={page} pageSize={pageSize} total={filtered.length} onPage={setPage} onPageSize={setPageSize} />
        </div>
    );
}
